import React, { useState } from "react";
import { AppBar, Toolbar, Menu, MenuItem, Badge } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import alterACELogo from "../assets/ACE Logo - alternate.png";
import { Box } from "@material-ui/core";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import useQuickChatIntro from "../hooks/useQuickChatIntro";

const useStyles = makeStyles((theme) => ({
  navLink: {
    fontFamily: "Poppins, sans-serif",
    marginLeft: "0.6em",
    color: "rgba(255,255,255,0.65)",
    textDecoration: "none",
    fontSize: "0.9em",
    fontWeight: 400,
    padding: "6px 14px",
    borderRadius: "20px",
    transition: "all 0.2s ease",
    "&:hover": {
      color: "white",
      backgroundColor: "rgba(255,255,255,0.08)",
    },
  },
  activeNavLink: {
    color: "#000",
    fontWeight: 600,
    backgroundColor: "#FFC629",
    "&:hover": {
      backgroundColor: "#FFC629",
      color: "#000",
    },
  },
  clickableLogo: {
    cursor: "pointer",
    transition: "opacity 0.2s ease",
    "&:hover": {
      opacity: 0.8,
    },
  },
}));

function Navbar({ onNewChat, onMenuClick, showMenuButton }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const userRole = localStorage.getItem("role");
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const { attentionSeen: quickChatAttentionSeen, markAttentionSeen } = useQuickChatIntro();

  const handleMenuClick = () => {
    // First sidebar open = buzzer has done its job. Stop pulsing. The
    // educational callout in the sidebar is dismissed separately.
    if (!quickChatAttentionSeen) markAttentionSeen();
    onMenuClick?.();
  };

  const isMainChatPage = location.pathname.startsWith("/main-chat");

  const handleLogoClick = () => {
    if (isMainChatPage && onNewChat) {
      onNewChat();
    } else {
      navigate("/main-chat");
    }
  };

  const handleManagementMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleManagementMouseLeave = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    setAnchorEl(null);
  };

  return (
    <AppBar
      position="fixed"
      style={{ backgroundColor: "#1a1a1a", height: "5rem" }}
    >
      <Toolbar
        style={{ justifyContent: "space-between", position: "relative" }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {showMenuButton && (
            <Badge
              data-testid="quick-chat-intro-badge"
              invisible={quickChatAttentionSeen}
              overlap="circular"
              variant="dot"
              sx={{
                mr: 2,
                "& .MuiBadge-badge": {
                  backgroundColor: "#FFC629",
                  color: "#FFC629",
                  boxShadow: "0 0 0 2px #1a1a1a",
                  top: 8,
                  right: 8,
                  // Only run the pulse animation when the dot is meant to be
                  // visible. A keyframed animation that defines `transform`
                  // would otherwise override MUI's scale(0)-on-invisible rule
                  // and keep the dot on screen.
                  animation: quickChatAttentionSeen
                    ? "none"
                    : "quickChatPulse 1.6s ease-in-out infinite",
                },
                "@keyframes quickChatPulse": {
                  "0%, 100%": {
                    boxShadow:
                      "0 0 0 2px #1a1a1a, 0 0 0 0 rgba(255, 198, 41, 0.65)",
                    transform: "scale(1)",
                  },
                  "70%": {
                    boxShadow:
                      "0 0 0 2px #1a1a1a, 0 0 0 8px rgba(255, 198, 41, 0)",
                    transform: "scale(1.15)",
                  },
                },
              }}
            >
              <IconButton onClick={handleMenuClick} sx={{ color: "#FFA629" }} data-tour="nav-menu-button">
                <MenuIcon />
              </IconButton>
            </Badge>
          )}
          <Box
            style={{ display: "flex", alignSelf: "flex-end" }}
          >
            <img
              src={alterACELogo}
              alt="ACE Logo"
              style={{ width: 260, height: "auto" }}
              onClick={handleLogoClick}
              className={classes.clickableLogo}
              title={isMainChatPage ? "Start New Chat" : "Go to Main Page"}
            />
          </Box>
        </Box>
        <Box
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        ></Box>
        <Box>
          <NavLink
            to="/main-chat"
            className={({ isActive }) =>
              isActive
                ? `${classes.navLink} ${classes.activeNavLink}`
                : classes.navLink
            }
          >
            Chat
          </NavLink>
          <NavLink
            to="/collections"
            className={({ isActive }) =>
              isActive
                ? `${classes.navLink} ${classes.activeNavLink}`
                : classes.navLink
            }
            data-tour="nav-collections-link"
          >
            Collections
          </NavLink>
          {userRole === "tenant admin" && (
            <NavLink
              to="/user-management"
              className={({ isActive }) =>
                isActive
                  ? `${classes.navLink} ${classes.activeNavLink}`
                  : classes.navLink
              }
              data-tour="nav-management-link"
            >
              Management
            </NavLink>
          )}
          {userRole === "sys admin" && (
            <>
              <Box
                component="span"
                className={
                  location.pathname === "/user-management" ||
                  location.pathname === "/tenant-management"
                    ? `${classes.navLink} ${classes.activeNavLink}`
                    : classes.navLink
                }
                onMouseEnter={handleManagementMouseEnter}
                sx={{ cursor: "pointer" }}
                data-tour="nav-management-link"
              >
                Management
              </Box>
              <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleManagementMouseLeave}
                MenuListProps={{
                  onMouseLeave: handleManagementMouseLeave,
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                PaperProps={{
                  sx: {
                    backgroundColor: "#303030",
                    color: "white",
                    marginTop: "8px",
                  },
                }}
              >
                <MenuItem
                  onClick={() => handleMenuItemClick("/user-management")}
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "0.95em",
                    "&:hover": {
                      backgroundColor: "#404040",
                    },
                  }}
                >
                  User Management
                </MenuItem>
                <MenuItem
                  onClick={() => handleMenuItemClick("/tenant-management")}
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "0.95em",
                    "&:hover": {
                      backgroundColor: "#404040",
                    },
                  }}
                >
                  Tenant Management
                </MenuItem>
              </Menu>
            </>
          )}
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              isActive
                ? `${classes.navLink} ${classes.activeNavLink}`
                : classes.navLink
            }
            data-tour="nav-settings-link"
          >
            Settings
          </NavLink>
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              isActive
                ? `${classes.navLink} ${classes.activeNavLink}`
                : classes.navLink
            }
            data-tour="nav-profile-link"
          >
            Profile
          </NavLink>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default Navbar;
